/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useMemo, useState, useCallback, ReactNode } from "react";
import _ from "lodash";
import env from "react-dotenv";
import { useSelector, useDispatch } from "react-redux";
import { StoreType } from "../../types/genericTypes";

// react-router-dom components
import { useLocation, NavLink, useNavigate } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "../../hocs/useRedux";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import AppsIcon from "@mui/icons-material/Dashboard";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 PRO React TS examples components
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
import SidenavList from "examples/Sidenav/SidenavList";
import SidenavItem from "examples/Sidenav/SidenavItem";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";

import AdbIcon from "@mui/icons-material/Adb";
import MenuIcon from "@mui/icons-material/Menu";

import GlobalStyles from "@mui/material/GlobalStyles";

import logo from "assets/images/arjion_b.png";

// Material Dashboard 2 PRO React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
  setDarkMode,
  setFixedNavbar,
  setSidenavColor,
} from "context";
import ModalComponent from "components/Modal";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  SpeedDial,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import MenuHome from "components/MenuHome/MenuHome";
import MobileMenu from "./MobileMenu";

// Declaring props types for Sidenav
interface Props {
  color?: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "dark";
  brand?: string;
  brandName: string;
  routes: {
    [key: string]:
    | ReactNode
    | string
    | {
      [key: string]:
      | ReactNode
      | string
      | {
        [key: string]: ReactNode | string;
      }[];
    }[];
  }[];
  [key: string]: any;
}

function Sidenav({ color, brand, brandName, routes, ...rest }: Props): JSX.Element {
  const navigate = useNavigate();
  const dispatch_ = useAppDispatch();
  const userName = useSelector((state: StoreType) => state?.app?.user?.data?.usuario || "");
  const nombre = useSelector((state: StoreType) => state?.app?.user?.data?.nombre || "");
  const fotoPerfil = useSelector((state: StoreType) => state?.app?.user?.data?.imagen || "");
  const idPerfilUsuario = useAppSelector((state) => state?.app?.user?.data?.id_perfil || 0);
  const id_usuario = useAppSelector((state) => state?.app?.user?.data?.id || 0);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [mensajeAlert, setMensajeAlert] = useState("");
  const handleisAlertOpen = () => setIsAlertOpen(true);
  const handleisAlerClose = () => {
    navigate(`/inicio`);
    setIsAlertOpen(false);
  };

  const tipoUsuario = Number(idPerfilUsuario) || 0;
  const puedeVer = (r: any): boolean =>
    !tipoUsuario || !Array.isArray(r?.allow) || r.allow.includes(tipoUsuario);

  /* PARA NUEVO MENÚ */
  const pages = ["Products", "Pricing", "Blog"];

  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const [anchorElMenus, setAnchorElMenus] = useState<Record<string, HTMLElement | null>>({});
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleOpenSubMenu = (key: string, event: React.MouseEvent<HTMLElement>) => {
    setAnchorElMenus((prev) => ({ ...prev, [key]: event.currentTarget }));
  };

  const handleCloseSubMenu = (key: string) => {
    setAnchorElMenus((prev) => ({ ...prev, [key]: null }));
  };

  /* --------------------------- */
  const [isAlertMenuOpen, setIsAlertMenuOpen] = useState(false);
  const handleisAlertMenuOpen = () => setIsAlertMenuOpen(true);
  const handleisAlertMenuClose = () => setIsAlertMenuOpen(false);

  const [openCollapse, setOpenCollapse] = useState<boolean | string>(false);
  const [openNestedCollapse, setOpenNestedCollapse] = useState<boolean | string>(false);
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } = controller;
  const location = useLocation();
  const { pathname } = location;
  const collapseName = useMemo(() => {
    return pathname.split("/").slice(1)[0];
  }, [pathname]);
  const items = pathname.split("/").slice(1);
  const itemParentName = useMemo(() => {
    return items[1];
  }, [items]);
  const itemName = items[items.length - 1];

  let textColor:
    | "primary"
    | "secondary"
    | "info"
    | "success"
    | "warning"
    | "error"
    | "dark"
    | "white"
    | "inherit"
    | "text"
    | "light" = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    setOpenCollapse(collapseName);
    setOpenNestedCollapse(itemParentName);
  }, [collapseName, itemParentName]);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    //handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location, transparentSidenav, whiteSidenav]);

  const perfilRoute = routes.find((r: any) => r.key === "perfil") as any;
  const settings = ((perfilRoute?.collapse as any[]) || []).filter(puedeVer);

  // Render all the nested collapse items from the routes.js
  const renderNestedCollapse = (collapse: any) => {
    const template = collapse
      .filter(puedeVer)
      .map(({ name, route, key, href }: any) =>
        href ? (
          <Link
            key={key}
            href={href}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavItem name={name} nested />
          </Link>
        ) : (
          <NavLink to={route} key={key} style={{ textDecoration: "none" }}>
            <SidenavItem name={name} active={route === pathname} nested />
          </NavLink>
        )
      );

    return template;
  };
  // Render the all the collpases from the routes.js
  const renderCollapse = (collapses: any) =>
    collapses
      .filter((c: any) => c?.key !== "perfil" && puedeVer(c))
      .map(({ name, collapse, route, href, key }: any) => {
        let returnValue;

        if (collapse) {
          returnValue = (
            <SidenavItem
              key={key}
              color={color}
              name={name}
              active={key === itemParentName ? "isParent" : false}
              open={openNestedCollapse === key}
              onClick={({ currentTarget }: any) =>
                openNestedCollapse === key && currentTarget.classList.contains("MuiListItem-root")
                  ? setOpenNestedCollapse(false)
                  : setOpenNestedCollapse(key)
              }
            >
              {renderNestedCollapse(collapse)}
            </SidenavItem>
          );
        } else {
          returnValue = href ? (
            <Link
              href={href}
              key={key}
              target="_blank"
              rel="noreferrer"
              sx={{ textDecoration: "none" }}
            >
              <SidenavItem color={color} name={name} active={key === itemName} />
            </Link>
          ) : (
            <NavLink to={route} key={key} style={{ textDecoration: "none" }}>
              <SidenavItem color={color} name={name} active={key === itemName} />
            </NavLink>
          );
        }
        return <SidenavList key={key}>{returnValue}</SidenavList>;
      });

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes
    .filter((c: any) => c?.key !== "perfil" && puedeVer(c))
    .map(({ type, name, icon, title, collapse, noCollapse, key, href, route }: any) => {
      let returnValue;
      const isActive = route === pathname;

      if (type === "collapse") {
        if (href) {
          // Enlace
          returnValue = (
            <Button
              key={key}
              href={href}
              target="_blank"
              rel="noreferrer"
              sx={{ mt: 0, color: "#fff !important", fontWeight: 600 }}
              startIcon={icon}
            >
              {name}
            </Button>
          );
        } else if (noCollapse && route) {
          // sin submenú
          returnValue = (
            <Button
              key={key}
              onClick={() => navigate(route)}
              sx={{
                position: "relative",
                display: "block",
                color: isActive ? "#95a5a6" : "#ecf0f1",
                textDecoration: "none",
                textTransform: "uppercase",
                transition: "0.5s",
                "&::after": {
                  position: "absolute",
                  content: '""',
                  top: "100%",
                  left: 0,
                  width: "100%",
                  height: "3px",
                  background: "#3498db",
                  transform: isActive ? "scaleX(1)" : "scaleX(0)",
                  transformOrigin: isActive ? "left" : "right",
                  transition: "transform 0.5s",
                },
                "&:hover": {
                  color: "#95a5a6",
                },
                "&:focus:not(:hover)": {
                  color: "#95a5a6",
                },
                "&:hover::after": {
                  transform: "scaleX(1)",
                  transformOrigin: "left",
                },
              }}
              startIcon={icon}
            >
              {name}
            </Button>
          );
        } else if (collapse) {
          const rutaActivaEnSubmenu = (items: any[]): boolean =>
            items.some(
              (item: any) =>
                item.route === pathname || (item.collapse && rutaActivaEnSubmenu(item.collapse))
            );

          const isActiveInventarios = rutaActivaEnSubmenu(collapse);
          // submenú desplegable
          returnValue = (
            <Box key={key}>
              <Button
                onClick={(e) => handleOpenSubMenu(key, e)}
                sx={{
                  position: "relative",
                  //display: "block",
                  color: isActiveInventarios ? "#95a5a6" : "#ecf0f1",
                  textDecoration: "none",
                  textTransform: "uppercase",
                  transition: "0.5s",
                  "&::after": {
                    position: "absolute",
                    content: '""',
                    top: "100%",
                    left: 0,
                    width: "100%",
                    height: "3px",
                    background: "#3498db",
                    transform: isActiveInventarios ? "scaleX(1)" : "scaleX(0)",
                    transformOrigin: isActiveInventarios ? "left" : "right",
                    transition: "transform 0.5s",
                  },
                  "&:hover": {
                    color: "#95a5a6",
                  },
                  "&:focus:not(:hover)": {
                    color: "#95a5a6",
                  },
                  "&:hover::after": {
                    transform: "scaleX(1)",
                    transformOrigin: "left",
                  },
                }}
                startIcon={icon}
                endIcon={<Icon>expand_more</Icon>}
              >
                {name}
              </Button>
              <Menu
                anchorEl={anchorElMenus[key]}
                open={Boolean(anchorElMenus[key])}
                onClose={() => handleCloseSubMenu(key)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{ sx: { borderRadius: 0 } }}
              >
                {collapse
                  .filter(puedeVer)
                  .map((child: any) =>
                    child.collapse ? (
                      // Nivel 2 con su propio submenú desplegable
                      <Box key={child.key}>
                        <MenuItem
                          onClick={(e) => handleOpenSubMenu(child.key, e)}
                          sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}
                        >
                          <Typography
                            variant="button"
                            sx={{ color: "#000", fontWeight: 600, fontSize: "12px" }}
                          >
                            {child.name}
                          </Typography>
                          <Icon fontSize="small">chevron_right</Icon>
                        </MenuItem>
                        <Menu
                          anchorEl={anchorElMenus[child.key]}
                          open={Boolean(anchorElMenus[child.key])}
                          onClose={() => handleCloseSubMenu(child.key)}
                          anchorOrigin={{ vertical: "top", horizontal: "right" }}
                          transformOrigin={{ vertical: "top", horizontal: "left" }}
                        >
                          {child.collapse
                            .filter(puedeVer)
                            .map((nested: any) => (
                              <MenuItem
                                key={nested.key}
                                onClick={() => {
                                  navigate(nested.route);
                                  handleCloseSubMenu(child.key);
                                  handleCloseSubMenu(key);
                                }}
                              >
                                <Typography
                                  variant="button"
                                  sx={{ color: "#000", fontWeight: 600, fontSize: "12px" }}
                                >
                                  {nested.name}
                                </Typography>
                              </MenuItem>
                            ))}
                        </Menu>
                      </Box>
                    ) : (
                      <MenuItem
                        key={child.key}
                        selected={child.route === pathname}
                        onClick={() => {
                          navigate(child.route);
                          handleCloseSubMenu(key);
                        }}
                      >
                        <Typography
                          variant="button"
                          sx={{ color: "#000", fontWeight: 600, fontSize: "12px" }}
                        >
                          {child.name}
                        </Typography>
                      </MenuItem>
                    )
                  )}
              </Menu>
            </Box>
          );
        }
      } else if (type === "title") {
        returnValue = (
          <MDTypography
            key={key}
            color={textColor}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </MDTypography>
        );
      } else if (type === "divider") {
        returnValue = (
          <Divider
            key={key}
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
          />
        );
      }

      return returnValue;
    });

  return (
    <>
      <AppBar position="static">
        <Container
          maxWidth="xl"
          sx={{
            background: { xs: "#fff", md: "#041d45" },
            display: { xs: "none", md: "block" },
            borderRadius: "40px",
            boxShadow: "0 10px 40px rgba(159, 162, 177, 0.8)",
            mt: 2,
            p: 1,
          }}
        >
          <Toolbar disableGutters>
            <img
              src={logo}
              onClick={(e: any) => {
                navigate("/inicio");
              }}
              alt={"Compras"}
              style={{
                width: 60,
                cursor: "pointer",
              }}
            />
            <Box sx={{ flexGrow: 1, display: { xs: "flex", md: "none" } }}>
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                color="inherit"
              >
                <MenuIcon />
              </IconButton>
            </Box>
            <Box sx={{ flexGrow: 1, display: { xs: "none", md: "flex" }, gap: 1 }}>
              {renderRoutes}
            </Box>
            <Box sx={{ flexGrow: 0 }}>
              <Tooltip title="Perfil">
                <IconButton
                  sx={{ display: "flex", flexDirection: "column", alignItems: "center", p: 0 }}
                >
                  <Avatar
                    alt="Imagen perfil"
                    src={`${env.ARJION_URL_ASSETS}${fotoPerfil?.replace(/^\.\./, "")}`}
                  />
                  <Typography
                    variant="button"
                    sx={{
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: "12px",
                      textAlign: "center",
                    }}
                  >
                    {nombre === "Lucero" ? "Compras" : nombre}
                  </Typography>
                </IconButton>
              </Tooltip>
              <Menu
                sx={{ mt: "45px" }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                {settings.map((setting: any) => (
                  <MenuItem
                    key={setting.key}
                    onClick={() => {
                      navigate(setting.route);
                      handleCloseUserMenu();
                    }}
                  >
                    {setting.icon && (
                      <Box sx={{ mr: 1, display: "flex", alignItems: "center" }}>
                        {setting.icon}
                      </Box>
                    )}
                    <Typography
                      variant="button"
                      sx={{ color: "#fff", fontWeight: 600, fontSize: "12px" }}
                    >
                      {setting.name}
                    </Typography>
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          </Toolbar>
        </Container>
        {/* Mobile */}
        <Container
          maxWidth="xl"
          sx={{
            display: { xs: "block", md: "none" },
          }}
        >
          <MobileMenu routes={routes} tipoUsuario={tipoUsuario} />
        </Container>
      </AppBar>
    </>
  );
}

// Declaring default props for Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

export default Sidenav;
